import React, { useState, useEffect } from 'react';
import {Link as RouterLink} from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControlLabel,
  Switch,
  InputAdornment,
  Breadcrumbs,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { membershipTierAPI } from '../../services/api';
import { AdditionalPerks } from '../../types';

// Import the MembershipTier interface from types
import { MembershipTier } from '../../types';

interface MembershipFormData {
  name: string;
  monthlyPrice: number;
  description: string;
  active: boolean;
  visible: boolean;
  features: string[];
  currentFeature?: string;
  amenityBookingsLimit: number | null;
  unlimitedAmenityBookings: boolean;
  classBookingsLimit: number | null;
  unlimitedClassBookings: boolean;
  initiationFee: number;
  durationMonths: number;
  additionalPerks: AdditionalPerks[];
  displayOrder: number;
}

const initialFormData: MembershipFormData = {
  name: '',
  monthlyPrice: 0,
  description: '',
  active: true,
  visible: true,
  features: [],
  currentFeature: '',
  amenityBookingsLimit: null,
  unlimitedAmenityBookings: false,
  classBookingsLimit: null,
  unlimitedClassBookings: false,
  initiationFee: 0,
  durationMonths: 1,
  additionalPerks: [],
  displayOrder: 100
};

const MembershipTierPage: React.FC = () => {
  const [memberships, setMemberships] = useState<MembershipTier[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [currentMembershipId, setCurrentMembershipId] = useState<string | null>(null);
  const [formData, setFormData] = useState<MembershipFormData>(initialFormData);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  // Fetch all membership tiers  
  const fetchMemberships = async () => {
    setLoading(true);
    try {
      // Set showAll to true to fetch all tiers including inactive ones
      const response = await membershipTierAPI.getMembershipTiers(true);
      // Sort membership tiers by displayOrder
      const sortedMemberships = [...response.data].sort((a, b) => a.displayOrder - b.displayOrder);
      setMemberships(sortedMemberships);
      setError(null);
    } catch (err) {
      console.error('Error fetching membership tiers:', err);
      setError('Failed to load membership tiers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMemberships();
  }, []);

  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Handle price field changes specifically for numeric values
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string, or valid numbers
    if (value === '' || !isNaN(Number(value))) {
      setFormData({
        ...formData,
        monthlyPrice: value === '' ? 0 : Number(value)
      });
    }
  };

  // Open dialog for creating a new membership tier
  const handleOpenCreateDialog = () => {
    setFormData(initialFormData);
    setEditMode(false);
    setCurrentMembershipId(null);
    setDialogOpen(true);
  };  // Open dialog for editing an existing membership tier  
  const handleOpenEditDialog = (membership: MembershipTier) => {
    setFormData({
      name: membership.name,
      monthlyPrice: membership.monthlyPrice,
      description: membership.description || '',
      active: membership.active,
      visible: membership.visible,
      features: Array.isArray(membership.features) ? [...membership.features] : [],
      currentFeature: '',
      amenityBookingsLimit: membership.amenityBookingsLimit || null,
      unlimitedAmenityBookings: membership.unlimitedAmenityBookings,
      classBookingsLimit: membership.classBookingsLimit || null,
      unlimitedClassBookings: membership.unlimitedClassBookings,
      initiationFee: membership.initiationFee,
      durationMonths: membership.durationMonths || 1,
      additionalPerks: membership.additionalPerks || [],
      displayOrder: membership.displayOrder || 100
    });
    setEditMode(true);
    setCurrentMembershipId(membership.id);
    setDialogOpen(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDeleteDialogOpen(false);
  };

  // Handle submit for creating or updating a membership tier
  const handleSubmit = async () => {
    try {
      // Validate form
      if (!formData.name.trim()) {
        setError('Membership name is required');
        return;
      }

      if (formData.monthlyPrice < 0) {
        setError('Price cannot be negative');
        return;
      }      // Prepare the data for submission
      const submissionData = {
        ...formData,
        // Ensure features is always an array, even if empty
        features: Array.isArray(formData.features) ? formData.features : [],
        // Remove the currentFeature field as it's only used for the form UI
        currentFeature: undefined
      };

      if (editMode && currentMembershipId) {
        // Update existing membership tier
        await membershipTierAPI.updateMembershipTier(currentMembershipId, submissionData);
        setSuccess('Membership tier updated successfully!');
      } else {
        // Create new membership tier
        await membershipTierAPI.createMembershipTier(submissionData);
        setSuccess('Membership tier created successfully!');
      }

      // Close dialog and refresh data
      setDialogOpen(false);
      fetchMemberships();

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 5000);
    } catch (err) {
      console.error('Error saving membership tier:', err);
      setError('Failed to save membership tier. Please try again.');
    }
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (id: string) => {
    setCurrentMembershipId(id);
    setDeleteDialogOpen(true);
  };

  // Handle delete membership tier
  const handleDelete = async () => {
    if (!currentMembershipId) return;

    try {
      await membershipTierAPI.deleteMembershipTier(currentMembershipId);
      setSuccess('Membership tier deleted successfully!');
      setDeleteDialogOpen(false);
      fetchMemberships();

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 5000);
    } catch (err) {
      console.error('Error deleting membership tier:', err);
      setError('Failed to delete membership tier. It may be in use by members.');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <RouterLink
          to="/dashboard">
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Dashboard
        </RouterLink>
        <Typography color="text.primary">Membership Tiers</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Membership Tiers
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Create New Tier
        </Button>
      </Box>

      {/* Success message */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main content */}
      <Paper sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : memberships.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="body1">
              No membership tiers found. Click "Create New Tier" to add one.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>              <TableHead>
                <TableRow>
                  <TableCell>Display Order</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="right">Monthly Price</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Visibility</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>                {memberships.map((membership) => (
                  <TableRow key={membership.id}>
                    <TableCell>{membership.displayOrder}</TableCell>
                    <TableCell>{membership.name}</TableCell>
                    <TableCell align="right">
                      ${membership.monthlyPrice}
                    </TableCell>
                    <TableCell>
                      {membership.description ?
                        (membership.description.length > 100
                          ? `${membership.description.substring(0, 100)}...`
                          : membership.description)
                        : 'No description'
                      }
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          backgroundColor: membership.active ? 'success.light' : 'error.light',
                          color: membership.active ? 'success.dark' : 'error.dark',
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          display: 'inline-block'
                        }}
                      >
                        {membership.active ? 'Active' : 'Inactive'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          backgroundColor: membership.visible ? 'info.light' : 'text.disabled',
                          color: membership.visible ? 'info.dark' : 'text.primary',
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          display: 'inline-block'
                        }}
                      >
                        {membership.visible ? 'Visible' : 'Hidden'}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        startIcon={<EditIcon />}
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => handleOpenEditDialog(membership)}
                      >
                        Edit
                      </Button>
                      <Button
                        startIcon={<DeleteIcon />}
                        color="error"
                        size="small"
                        onClick={() => handleOpenDeleteDialog(membership.id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} disableEscapeKeyDown={true} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Membership Tier' : 'Create Membership Tier'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {editMode
              ? 'Update the membership tier details below.'
              : 'Enter the details for the new membership tier.'}
          </DialogContentText>          <Grid container spacing={2}>            {/* Basic Information */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" gutterBottom>Basic Information</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                name="name"
                label="Membership Name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                required
                margin="dense"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                name="displayOrder"
                label="Display Order"
                value={formData.displayOrder}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || !isNaN(Number(value))) {
                    setFormData({
                      ...formData,
                      displayOrder: value === '' ? 100 : Number(value)
                    });
                  }
                }}
                fullWidth
                margin="dense"
                type="number"
                helperText="Lower numbers appear first. Must be unique."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                name="monthlyPrice"
                label="Monthly Price"
                value={formData.monthlyPrice}
                onChange={handlePriceChange}
                fullWidth
                required
                margin="dense"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                name="initiationFee"
                label="Initiation Fee"
                value={formData.initiationFee}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || !isNaN(Number(value))) {
                    setFormData({
                      ...formData,
                      initiationFee: value === '' ? 0 : Number(value)
                    });
                  }
                }}
                fullWidth
                margin="dense"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                name="durationMonths"
                label="Duration (Months)"
                value={formData.durationMonths}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || !isNaN(Number(value))) {
                    setFormData({
                      ...formData,
                      durationMonths: value === '' ? 1 : Number(value)
                    });
                  }
                }}
                fullWidth
                margin="dense"
                type="number"
                InputProps={{
                  inputProps: { min: 1 }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={handleChange}
                      name="active"
                    />
                  }
                  label="Active"
                  sx={{ mr: 4 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.visible}
                      onChange={handleChange}
                      name="visible"
                    />
                  }
                  label="Visible to Members"
                />
              </Box>
            </Grid>

            {/* Description */}
            <Grid size={{ xs: 12 }}>
              <TextField
                name="description"
                label="Description"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                margin="dense"
                multiline
                rows={4}
              />
            </Grid>

            {/* Amenity Booking Settings */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>Amenity Booking Settings</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                name="amenityBookingsLimit"
                label="Monthly Amenity Booking Limit"
                value={formData.amenityBookingsLimit === null ? '' : formData.amenityBookingsLimit}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || !isNaN(Number(value))) {
                    setFormData({
                      ...formData,
                      amenityBookingsLimit: value === '' ? null : Number(value),
                      unlimitedAmenityBookings: value === '' ? true : false
                    });
                  }
                }}
                fullWidth
                margin="dense"
                type="number"
                disabled={formData.unlimitedAmenityBookings}
                helperText="Leave empty for unlimited bookings"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.unlimitedAmenityBookings}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          unlimitedAmenityBookings: e.target.checked,
                          amenityBookingsLimit: e.target.checked ? null : formData.amenityBookingsLimit || 0
                        });
                      }}
                      name="unlimitedAmenityBookings"
                    />
                  }
                  label="Unlimited Amenity Bookings"
                />
              </Box>
            </Grid>

            {/* Class Booking Settings */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>Class Booking Settings</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                name="classBookingsLimit"
                label="Monthly Class Booking Limit"
                value={formData.classBookingsLimit === null ? '' : formData.classBookingsLimit}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || !isNaN(Number(value))) {
                    setFormData({
                      ...formData,
                      classBookingsLimit: value === '' ? null : Number(value),
                      unlimitedClassBookings: value === '' ? true : false
                    });
                  }
                }}
                fullWidth
                margin="dense"
                type="number"
                disabled={formData.unlimitedClassBookings}
                helperText="Leave empty for unlimited bookings"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.unlimitedClassBookings}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          unlimitedClassBookings: e.target.checked,
                          classBookingsLimit: e.target.checked ? null : formData.classBookingsLimit || 0
                        });
                      }}
                      name="unlimitedClassBookings"
                    />
                  }
                  label="Unlimited Class Bookings"
                />
              </Box>
            </Grid>

            {/* Features */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>Features</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  name="features"
                  label="Features (press Enter to add)"
                  value={formData.currentFeature || ''}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      currentFeature: e.target.value
                    });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && formData.currentFeature && formData.currentFeature.trim() !== '') {
                      e.preventDefault();
                      const newFeature = formData.currentFeature.trim();
                      setFormData({
                        ...formData,
                        features: [...formData.features, newFeature],
                        currentFeature: ''
                      });
                    }
                  }}
                  fullWidth
                  margin="dense"
                  helperText="Type a feature and press Enter to add it to the list"
                />
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 1, height: 40 }}
                  onClick={() => {
                    if (formData.currentFeature && formData.currentFeature.trim() !== '') {
                      const newFeature = formData.currentFeature.trim();
                      setFormData({
                        ...formData,
                        features: [...formData.features, newFeature],
                        currentFeature: ''
                      });
                    }
                  }}
                >
                  Add
                </Button>
              </Box>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                {formData.features.map((feature, index) => (
                  <Chip
                    key={index}
                    label={feature}
                    onDelete={() => {
                      const newFeatures = [...formData.features];
                      newFeatures.splice(index, 1);
                      setFormData({
                        ...formData,
                        features: newFeatures
                      });
                    }}
                  />
                ))}
              </Stack>
            </Grid>

            {/* Additional Perks */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>Additional Perks</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      additionalPerks: [
                        ...formData.additionalPerks,
                        { name: '', description: '', icon: '' }
                      ]
                    });
                  }}
                  startIcon={<AddIcon />}
                >
                  Add Perk
                </Button>
              </Box>
              
              {formData.additionalPerks.map((perk, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    border: '1px solid', 
                    borderColor: 'divider', 
                    p: 2, 
                    mb: 2,
                    borderRadius: 1
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2">Perk #{index + 1}</Typography>
                    <Button 
                      size="small" 
                      color="error" 
                      onClick={() => {
                        const newPerks = [...formData.additionalPerks];
                        newPerks.splice(index, 1);
                        setFormData({
                          ...formData,
                          additionalPerks: newPerks
                        });
                      }}
                      startIcon={<DeleteIcon />}
                    >
                      Remove
                    </Button>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        margin="dense"
                        label="Perk Name"
                        value={perk.name}
                        onChange={(e) => {
                          const newPerks = [...formData.additionalPerks];
                          newPerks[index] = { ...perk, name: e.target.value };
                          setFormData({
                            ...formData,
                            additionalPerks: newPerks
                          });
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        margin="dense"
                        label="Icon (optional)"                        value={perk.icon || ''}
                        onChange={(e) => {
                          const newPerks = [...formData.additionalPerks];
                          newPerks[index] = { ...perk, icon: e.target.value };
                          setFormData({
                            ...formData,
                            additionalPerks: newPerks
                          });
                        }}
                        helperText="Icon name or code (e.g., 'spa', 'fitness_center')"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        margin="dense"
                        label="Description (optional)"
                        value={perk.description || ''}
                        multiline
                        rows={2}
                        onChange={(e) => {
                          const newPerks = [...formData.additionalPerks];
                          newPerks[index] = { ...perk, description: e.target.value };
                          setFormData({
                            ...formData,
                            additionalPerks: newPerks
                          });
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
              
              {formData.additionalPerks.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
                  No additional perks added yet. Click "Add Perk" to add one.
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} disableEscapeKeyDown={true}>
        <DialogTitle>Delete Membership Tier</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this membership tier? This action cannot be undone.
            If members are currently using this tier, the deletion may fail.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MembershipTierPage;
